<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="organizationform"
            name="organizationForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-md-6">
                <h4 v-if="adminSettingsMode">{{ $t('labelOrganization') }}</h4>
                <!-- Name -->
                <div class="form-group">
                    <TextInput
                        :text="$t('columnNameName')"
                        v-model="record.name"
                        :id="'name'"
                        :name="'name'"
                        :state="!organizationNameInUse"
                        @input="$_exists"
                        :plaintext="adminSettingsMode === true"
                        :required="adminSettingsMode === false"
                        :invalidMessage="$t('organizationInUse')"
                        :maxlength="50"
                        autofocus
                    >
                    </TextInput>
                </div>
                <div
                    class="form-group"
                    v-if="!adminSettingsMode && userInfo.isSuper"
                >
                    <label for="tags-state-event">{{
                        $t('labelEnterDomainNames')
                    }}</label>
                    <b-form-tags
                        input-id="tags-state-event"
                        v-model="tags"
                        :tag-validator="validator"
                        :placeholder="$t('labelEnterDomainNamesInstruction')"
                        :invalidTagText="$t('labelInvalidDomainName')"
                        separator=" "
                    />
                </div>
                <!-- If Scim has been configured these setting are not needed -->
                <!-- Get proxyusers from AD, only if no ScimToken -->
                <div class="form-group" v-if="!record.scimToken">
                    <XCheckbox
                        :id="'getProxyUsersFromAd'"
                        :name="'getProxyUsersFromAd'"
                        :text="$t('labelgetProxyUsersFromAd')"
                        :value="record.getProxyUsersFromAd"
                        @input="$_toggleGetProxyUsersFromAd"
                    >
                    </XCheckbox>
                </div>
                <!-- Proxyuser domein filter, only if no ScimToken-->
                <div class="form-group" v-if="!record.scimToken">
                    <TextInput
                        :text="$t('labelProxyUserDomainFilter')"
                        v-model="record.proxyUserDomainFilter"
                        :id="'proxyUserDomainFilter'"
                        :name="'proxyUserDomainFilter'"
                        :maxlength="255"
                    >
                    </TextInput>
                </div>
                <!-- End Non scim settings -->
                <!-- Language -->
                <div class="form-group" v-if="userInfo.isSuper">
                    <BaseSelect
                        :id="'slcLanguage'"
                        :name="'slcLanguage'"
                        :text="$t('labelLanguage')"
                        :required="true"
                        :value="record.language"
                        :options="availableLanguages"
                        @selected="$_onLanguageSelected"
                    >
                    </BaseSelect>
                </div>
                <!-- Logo -->
                <div class="form-group">
                    <BaseSelect
                        :id="'slcLogo'"
                        :name="'slcLogo'"
                        :text="`${$t('labelLogo')}${$t('labelOptional')}`"
                        :value="record.logoId > 0 ? record.logoId : null"
                        :options="$_availableLogos(organizationBitmaps)"
                        @selected="$_onLogoSelected"
                    >
                    </BaseSelect>
                    <b-link
                        :id="`addLogo`"
                        :name="`addLogo`"
                        @click="
                            $_openAddBitmapModal(
                                availableLogoTypeConst[0].value
                            )
                        "
                    >
                        &nbsp;
                        <em class="fa fa-plus"></em>&nbsp;{{
                            $t('imageAddButton', [
                                `${$t('labelLogo').toLowerCase()}`
                            ])
                        }}
                    </b-link>
                </div>
                <!-- Favicon -->
                <div class="form-group">
                    <BaseSelect
                        :id="'slcFavicon'"
                        :name="'slcFavicon'"
                        :text="`${$t('labelFavicon')}${$t('labelOptional')}`"
                        :value="record.faviconId > 0 ? record.faviconId : null"
                        :options="$_availableFavicons(organizationBitmaps)"
                        @selected="$_onFaviconSelected"
                    >
                    </BaseSelect>
                    <b-link
                        :id="`addFavicon`"
                        :name="`addFavicon`"
                        @click="
                            $_openAddBitmapModal(
                                availableLogoTypeConst[1].value
                            )
                        "
                    >
                        &nbsp;
                        <em class="fa fa-plus"></em>&nbsp;{{
                            $t('imageAddButton', [
                                `${$t('labelFavicon').toLowerCase()}`
                            ])
                        }}
                    </b-link>
                </div>
                <!-- default Usergroup -->
                <div v-if="adminSettingsMode" class="form-group">
                    <BaseSelect
                        :id="'slcUsergroup'"
                        :name="'slcUsergroup'"
                        :text="$t('labelDefaultUserGroup')"
                        :required="false"
                        :value="record.userGroupId"
                        :options="this.userGroups"
                        @selected="$_onBaseUserGroupSelected($event)"
                    >
                    </BaseSelect>
                </div>
                <!-- default baseprofile -->
                <div
                    v-if="adminSettingsMode && !this.record.userGroupId"
                    class="form-group"
                >
                    <BaseSelect
                        :id="'slcProfile'"
                        :name="'slcProfileId'"
                        :text="$t('labelDefaultProfile')"
                        :required="false"
                        :value="record.profileId"
                        :options="
                            $_convertToValueTextPair(
                                this.baseProfiles,
                                'id',
                                'name'
                            )
                        "
                        @selected="$_onBaseProfileSelected($event)"
                    >
                    </BaseSelect>
                </div>
                <!-- Email error/reports -->
                <div class="form-group">
                    <EmailInput
                        :text="$t('labelEmailError')"
                        v-model="record.email"
                        :id="'email'"
                        :name="'email'"
                        :maxlength="255"
                        required
                        @input="$_fillNewsEmail"
                        :pattern="emailListRegex"
                        v-bind:validState.sync="validStateEmail"
                    >
                    </EmailInput>
                </div>
                <!-- Email on error only -->
                <div class="form-group">
                    <XCheckbox
                        :id="'emailOnErrorOnly'"
                        :name="'emailOnErrorOnly'"
                        :text="$t('labelEmailErrorOnly')"
                        :value="record.emailOnErrorOnly"
                        @input="$_toggleEmailOnErrorOnly"
                    >
                    </XCheckbox>
                </div>
                <!-- Nieuwsberichten Email-->
                <div class="form-group">
                    <EmailInput
                        :text="$t('labelEmailNewsItems')"
                        v-model="record.newsEmail"
                        :id="'newsemail'"
                        :name="'newsemail'"
                        :maxlength="255"
                        required
                        :pattern="emailListRegex"
                        v-bind:validState.sync="validStateNewsEmail"
                    >
                    </EmailInput>
                </div>
                <!-- Afzender Email -->
                <div class="form-group">
                    <EmailInput
                        :text="$t('labelEmailSenderFeedback')"
                        v-model="record.senderEmail"
                        :id="'senderemail'"
                        :name="'senderemail'"
                        :maxlength="50"
                        required
                        :pattern="emailRegex"
                        v-bind:validState.sync="validStateSenderEmail"
                        singleMode
                    >
                    </EmailInput>
                </div>
                <!-- Afzender Email naam -->
                <div class="form-group">
                    <TextInput
                        :text="$t('labelEmailSenderNameFeedback')"
                        v-model="record.senderEmailName"
                        :id="'senderemailname'"
                        :name="'senderemailname'"
                        :maxlength="255"
                        required
                    >
                    </TextInput>
                </div>
                <!-- Max features -->
                <div
                    class="form-group"
                    v-show="$route.name === 'organizationDetail'"
                >
                    <TextInput
                        :text="$t('labelMaxFilterResults')"
                        v-model="record.maxFeatures"
                        :id="'maxfeatures'"
                        :name="'maxfeatures'"
                        :placeholder="'100'"
                        :type="'number'"
                        :maxlength="5"
                        required
                        :pattern="maxFeaturesRegex"
                    >
                    </TextInput>
                </div>
                <!-- Max adressen Stuf search -->
                <div v-if="hasStufLicense" class="form-group">
                    <x-number
                        id="maxAdressenStuf"
                        v-model="record.maxAdressenStuf"
                        required
                        :minValue="0"
                        :maxValue="maxResults"
                        :step="1"
                        :text="$t('labelMaxAdressenStuf')"
                        placeholder="100"
                    >
                    </x-number>
                </div>
                <!-- Max adressen Kvk search -->
                <div v-if="hasKvkLicense" class="form-group">
                    <x-number
                        id="maxAdressenKvk"
                        v-model="record.maxAdressenKvk"
                        required
                        :minValue="0"
                        :maxValue="maxResults"
                        :step="1"
                        :text="$t('labelMaxAdressenKvk')"
                        placeholder="100"
                    >
                    </x-number>
                </div>
                <div v-if="adminSettingsMode">
                    <h4>
                        {{ $t('mainSettings.labelKaartMenu') }}
                    </h4>
                    <div class="form-group">
                        <TextInput
                            :text="$t('labelFeedbackLabelText')"
                            v-model="feedbackLabelText"
                            :id="'feedbackLabelText'"
                            :name="'feedbackLabeltext'"
                            maxlength="20"
                            :placeholder="$t('menuItemFeedbacks')"
                        >
                        </TextInput>
                    </div>
                </div>
                <!-- Bounding box -->
                <br />
                <h4>{{ $t('labelEnvelopeRect') }}</h4>
                <div class="form-group">
                    <label class="control-label" for="txtPoly">{{
                        $t('labelPolygonOptional')
                    }}</label>
                    <x-textarea
                        id="txtPoly"
                        name="txtPoly"
                        @input="$_isValidPolygon"
                        v-model="record.coordinates"
                        :hasResizeHandle="true"
                    >
                    </x-textarea>
                    <label v-show="errorInvalidFormat" class="errormsg"
                        >{{ $t('labelPolygonInvalid') }} <br />{{
                            $t('labelPolygonExample')
                        }}</label
                    >
                    <label v-show="errorInvalidGeom" class="errormsg"
                        >{{ $t('labelPolygonInvalid') }} <br />{{
                            $t('labelPolygonCheck')
                        }}</label
                    >
                </div>
                <!-- Bereken de rechthoek uit de polygoon -->
                <div class="form-group">
                    <b-button
                        :disabled="isBtnCalculateDisabled"
                        @click="$_calculateBBox"
                    >
                        {{ $t('labelPolygonCalculate') }}
                    </b-button>
                </div>
                <b-container>
                    <b-row>
                        <!-- Linker onderhoek -->
                        <b-col>
                            <TextInput
                                :text="$t('labelMinX')"
                                v-model="record.minX"
                                :id="'bbox_minx'"
                                :name="'bbox_minx'"
                                :placeholder="'0'"
                                :maxlength="255"
                                @input="$_validateBBox"
                                required
                                :type="'number'"
                                :pattern="ordinateRegex"
                            >
                            </TextInput>
                        </b-col>
                        <b-col>
                            <TextInput
                                :text="$t('labelMinY')"
                                v-model="record.minY"
                                :id="'bbox_miny'"
                                :name="'bbox_miny'"
                                :placeholder="'0'"
                                :maxlength="255"
                                @input="$_validateBBox"
                                required
                                :type="'number'"
                                :pattern="ordinateRegex"
                            >
                            </TextInput>
                        </b-col>
                        <!-- Rechter bovenhoek -->
                        <b-col>
                            <TextInput
                                :text="$t('labelMaxX')"
                                v-model="record.maxX"
                                :id="'bbox_maxx'"
                                :name="'bbox_maxx'"
                                :placeholder="'0'"
                                :maxlength="255"
                                @input="$_validateBBox"
                                required
                                :type="'number'"
                                :pattern="ordinateRegex"
                            >
                            </TextInput>
                        </b-col>
                        <b-col>
                            <TextInput
                                :text="$t('labelMaxY')"
                                v-model="record.maxY"
                                :id="'bbox_maxy'"
                                :name="'bbox_maxy'"
                                :placeholder="'0'"
                                :maxlength="255"
                                @input="$_validateBBox"
                                required
                                :type="'number'"
                                :pattern="ordinateRegex"
                            >
                            </TextInput>
                        </b-col>
                    </b-row>
                </b-container>
                <div class="form-group">
                    <!-- Invoer fout informatie -->
                    <label v-show="errorInvalidBBox" class="errormsg">{{
                        $t('labelBoundingBoxInvalid')
                    }}</label>
                </div>
                <!-- Highlight organization border -->
                <div class="form-group">
                    <XCheckbox
                        :id="'chkHighightBorder'"
                        :name="'chkHighightBorder'"
                        :text="$t('labelPolygonHighlight')"
                        :value="record.highlightBorder"
                        @input="$_toggleHighlightBorder"
                    >
                    </XCheckbox>
                </div>
                <!-- Feedback authentication -->
                <br />
                <h4>{{ menuItemStorageLayersTitle }}</h4>
                <div class="form-group">
                    <UsernamePassword
                        idPrefix="FeedBack"
                        :userNameReadonly="feedbackUserNameIsReadonly"
                        :passwordReadonly="feedbackUserPasswordIsReadonly"
                        :username="record.feedbackUsername"
                        :password="record.feedbackPassword"
                        :required="true"
                        @inputchanged="$_onAuthPasswordChanged"
                        @inputnamechanged="$_feedbackUserNameExists"
                        :pwtogglevisible="showEyeIcon"
                        :userStateValid="feedbackUserNameAvailable"
                    >
                    </UsernamePassword>
                </div>
                <br />
                <!-- NedGlobe Services connection -->
                <div v-if="!adminSettingsMode || record.hasServicesConnection">
                    <h4>{{ $t('menuItemNedGlobeServices') }}</h4>
                    <div class="form-group">
                        <div>
                            <x-checkbox
                                v-if="isCodeReadonly"
                                id="ServicesConnection"
                                name="ServicesConnection"
                                :text="
                                    $t('nedglobeServices.hasServicesConnection')
                                "
                                v-model="record.hasServicesConnection"
                            >
                            </x-checkbox>
                        </div>
                        <!-- Organization Id and Code -->
                        <div
                            class="form-group"
                            v-if="!adminSettingsMode && userInfo.isSuper"
                        >
                            <TextInput
                                v-if="superUserEditMode"
                                :text="$t('columnNameOrganizationId')"
                                v-model="record.id"
                                :id="'organizationId'"
                                :name="'organizationId'"
                                :readonly="true"
                            >
                            </TextInput>
                            <TextInput
                                :text="$t('columnNameCode')"
                                v-model="record.code"
                                :id="'code'"
                                :name="'code'"
                                :state="!organizationCodeInUse"
                                @input="$_codeExists"
                                :plaintext="adminSettingsMode === true"
                                :required="adminSettingsMode === false"
                                :invalidMessage="$t('organizationCodeInUse')"
                                :maxlength="20"
                                :readonly="isCodeReadonly"
                            >
                            </TextInput>
                        </div>
                        <div
                            class="form-group"
                            v-if="!adminSettingsMode && userInfo.isSuper"
                            v-show="record.hasServicesConnection"
                        >
                            <TextInput
                                :text="$t('columnNameStorageName')"
                                v-model="record.storageAccountName"
                                :id="'storageAccountName'"
                                :name="'storageAccountName'"
                                :readonly="true"
                                :maxlength="32"
                            >
                            </TextInput>
                            <b-card
                                :title="
                                    $t('nedglobeServices.getStorageAccountKey')
                                "
                            >
                                <b-card-text>
                                    1. az account set --subscription
                                    a8ac8cc1-f4e7-404e-9e99-c3fe2d9a5d19<br />
                                    2. az storage account keys list
                                    --account-name [storage-account naam uit het
                                    veld hier boven]<br />
                                    --resource-group [keuze uit:
                                    RG-NedGlobe-Prod-NG of RG-NedGlobe-Test-NG]
                                    --query [0].value --output tsv
                                </b-card-text>
                            </b-card>
                            <TextInput
                                :text="$t('columnNameStorageKey')"
                                v-model="record.storageAccountKey"
                                :id="'storageAccountKey'"
                                :name="'storageAccountKey'"
                                type="password"
                                :maxlength="128"
                            >
                            </TextInput>
                        </div>
                        <div v-show="record.hasServicesConnection">
                            <UsernamePassword
                                idPrefix="NedGlobeServices"
                                :username="servicesAuthentication.username"
                                :password="servicesAuthentication.password"
                                :required="record.hasServicesConnection"
                                @inputchanged="$_onServicesPasswordChanged"
                                @inputnamechanged="$_onServicesUsernameChanged"
                                :user-name-readonly="adminSettingsMode"
                                :readonly="!adminSettingsMode"
                                :pwtogglevisible="showServicesEyeIcon"
                            >
                            </UsernamePassword>

                            <div class="form-group">
                                <TextInput
                                    :text="$t('nedglobeServices.geoServerUrl')"
                                    v-model="
                                        servicesAuthentication.geoServerUrl
                                    "
                                    id="geoServerUrl"
                                    name="geoServerUrl"
                                    disabled="disabled"
                                    :maxlength="255"
                                >
                                </TextInput>
                                <base-button
                                    v-if="userInfo.isSuper"
                                    id="open-geoserver"
                                    size="sm"
                                    @click="$_onOpenGeoServer"
                                    :text="
                                        $t('nedglobeServices.btnOpenGeoServer')
                                    "
                                >
                                </base-button>
                            </div>
                        </div>
                    </div>
                    <br />
                </div>
                <h4>
                    {{
                        `${$t('mainSettings.labelPdok')} ${$t(
                            'mainSettings.labelPdokLocationServer'
                        )}`
                    }}
                </h4>
                <div class="form-group">
                    <TextInput
                        :text="$t('labelMunicipalities')"
                        :placeholder="$t('labelMunicipalitiesExample')"
                        v-model="record.locations"
                        :id="'plaatsen'"
                        :name="'plaatsen'"
                        maxlength="400"
                    >
                    </TextInput>
                </div>
                <div class="form-group">
                    <TextInput
                        :text="$t('labelProvinces')"
                        :placeholder="$t('labelProvincesExample')"
                        v-model="record.provinces"
                        :id="'provinces'"
                        :name="'provinces'"
                        maxlength="120"
                    >
                    </TextInput>
                </div>

                <!-- Licenses-->
                <div v-if="!adminSettingsMode">
                    <br />
                    <h4>{{ $t('labelLicenses') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        thead-class="d-none"
                        :tbody-tr-class="$_rowClass"
                        :items="licenses"
                        :fields="fieldsLicenses"
                        @row-clicked="$_toggleLicense"
                    >
                        <template v-slot:cell(hasLicense)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}hasLicense`"
                                v-bind:name="`${row.item.id}hasLicense`"
                                v-model="row.item.hasLicense"
                            >
                            </b-form-checkbox>
                        </template>
                    </b-table>
                </div>
                <!-- API licenses-->
                <div v-if="!adminSettingsMode && hasMobileLicense">
                    <br />
                    <h4>{{ $t('apiLicensesTitle') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        thead-class="d-none"
                        :tbody-tr-class="$_rowClass"
                        :items="localApiLicenses"
                        :fields="apiLicenseFields"
                    >
                        <template v-slot:cell(licensed)="row">
                            <b-form-checkbox
                                :id="`${row.item.name}licensed`"
                                :name="`${row.item.name}licensed`"
                                v-model="row.item.licensed"
                            >
                            </b-form-checkbox>
                        </template>
                    </b-table>
                </div>
                <!-- Url connection licenses-->
                <div v-if="!adminSettingsMode && hasMobileLicense">
                    <br />
                    <h4>{{ $t('urlConnectionLicenses') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        thead-class="d-none"
                        :tbody-tr-class="$_rowClass"
                        :items="urlConnectionLicenses"
                        :fields="urlConnectionLicensesFields"
                    >
                        <template v-slot:cell(licensed)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}licensed`"
                                v-bind:name="`${row.item.id}licensed`"
                                v-model="row.item.licensed"
                                @change="$_onChangeLicensed($event, row.item)"
                            >
                            </b-form-checkbox>
                        </template>
                    </b-table>
                </div>
                <!-- only show when there is no scimToken and
                 if in superusermode or azureTenant has been set
                 -->
                <h4
                    v-if="
                        !record.scimToken &&
                        (!adminSettingsMode || record.azureAdTenant)
                    "
                >
                    {{ $t('users.maintenance') }}
                </h4>
                <div v-if="record.azureAdTenant" class="form-group">
                    <TextInput
                        :text="$t('users.LabelUserDeleteWait')"
                        v-model="record.userDeleteWait"
                        :id="'userDeleteWait'"
                        :name="'userDeleteWait'"
                        :placeholder="'0'"
                        :maxlength="3"
                        :type="'number'"
                    >
                    </TextInput>
                </div>
                <!-- Only show for superuser and when there is no scimToken-->
                <div v-if="!adminSettingsMode && !record.scimToken">
                    <TextInput
                        :text="$t('LabelOrganizationAzureAdTenant')"
                        v-model="record.azureAdTenant"
                        :id="'AzureAdTenant'"
                        :name="'AzureAdTenant'"
                        :maxlength="255"
                    >
                    </TextInput>
                </div>
                <scim-token-selector
                    :id="userInfo.organizationId"
                    :scimToken="record.scimToken"
                    @updateToken="$_updateToken"
                />
                <!-- Save/cancel/remove buttons -->
                <SubmitCancelRemove
                    :is-saving="isSaving"
                    :is-deleting="isDeleting"
                    :mode="superUserEditMode ? modeEditConst : modeAddConst"
                    :isBtnSaveDisabled="isBtnSaveDisabled"
                    :isBtnRemoveDisabled="isBtnRemoveDisabled"
                    @cancel="$_onCancel"
                    @remove="$_onRemove"
                >
                </SubmitCancelRemove>
            </div>
        </b-form>
        <add-bitmap-modal
            id="orgAddBitmapModal"
            :launchModal="launchModal"
            :selectedImageType="modalSelectedImageType"
            :organizationId="
                this.superUserEditMode ? this.id : this.userInfo.organizationId
            "
            @modal-bitmap-saved="$_OnModalBitmapSaved"
        >
        </add-bitmap-modal>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import {
        availableFavicons,
        availableLogos,
        nprogress,
        arraySortAscendingFunction,
        convertToValueTextPair
    } from '../../services/utilities';
    import { orderBy, debounce } from 'lodash';
    import {
        AVAILABLE_LOGO_TYPE,
        MODE_ADD,
        MODE_EDIT,
        EXTERNAL_API_TYPE,
        MAX_RESULTS_RELATION
    } from '../../constants';

    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import BaseButton from '../../components/BaseComponents/base-button.vue';
    import TextInput from '../../components/BaseComponents/x-input.vue';
    import EmailInput from '../../components/EmailInput.vue';
    import UsernamePassword from '../../components/UsernamePassword.vue';
    import BaseSelect from '../../components/BaseComponents/base-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';
    import AddBitmapModal from '../bitmaps/AddBitmapModal.vue';
    import XNumber from '../../components/BaseComponents/x-number.vue';
    import ScimTokenSelector from './ScimTokenSelector.vue';

    export default {
        mixins: [modalMixin, refcodeMixin],

        components: {
            TextInput,
            EmailInput,
            UsernamePassword,
            BaseSelect,
            XCheckbox,
            'x-textarea': XTextarea,
            SubmitCancelRemove,
            'add-bitmap-modal': AddBitmapModal,
            'base-button': BaseButton,
            'x-number': XNumber,
            ScimTokenSelector
        },

        props: {
            id: { type: Number, required: false, default: -1 },
            isSettings: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                // Constants
                availableLogoTypeConst: AVAILABLE_LOGO_TYPE,
                modeAddConst: MODE_ADD,
                modeEditConst: MODE_EDIT,

                userInfo: {},
                record: {},
                organizationBitmaps: [],
                baseProfiles: [],
                domains: [],
                logo: {
                    id: 0,
                    name: '',
                    organizationId: 0,
                    imageData: null,
                    imageType: null
                },
                user: {},
                tags: [],
                userGroups: [],

                // Set to true when the superuser edits the organizations settings (from the 'Beheer' menu)
                superUserEditMode: false,
                // Set to true when the admin edits the organizations settings (from the 'System' menu)
                adminSettingsMode: false,

                launchModal: false,
                validStateEmail: true,
                validStateNewsEmail: true,
                validStateSenderEmail: true,
                availableLanguages: [],
                licenses: [],
                isSaving: false,
                isDeleting: false,
                isCodeReadonly: false,
                fieldsLicenses: [{ key: 'displayName' }, { key: 'hasLicense' }],
                urlConnectionLicensesFields: [
                    {
                        key: 'urlConnectionLicense.name',
                        tdClass: 'license-cell-width'
                    },
                    { key: 'licensed' }
                ],
                urlConnectionLicenses: [],
                // Regular expression for email and coordinates
                emailRegex:
                    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
                // eslint-disable-next-line max-len
                emailListRegex:
                    /^((?:(?:[a-zA-Z0-9_\-.]+)@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(?:(?:[a-zA-Z0-9-]+\.)+))(?:[a-zA-Z]{2,4}|[0-9]{1,3})(?:\]?)(?:\s*(;|,)\s*|\s*$))*)$/i,
                ordinateRegex: /^\d+(\.\d+)?$/,
                maxFeaturesRegex: /^\d{1,10}$/,

                // Is polygon valid?
                polygonIsValid: true,

                // Initieel de meldingen verbergen
                organizationNameInUse: false,
                organizationCodeInUse: false,
                feedbackUserNameAvailable: null,
                errorInvalidFormat: false,
                errorInvalidBBox: false,
                errorInvalidGeom: false,
                modalSelectedImageType: -1,
                apiLicenseFields: [
                    {
                        key: 'name',
                        tdClass: 'license-cell-width'
                    },
                    { key: 'licensed' }
                ],
                apiLicenses: [],
                localApiLicenses: [],
                servicesAuthentication: {
                    geoServerUrl: null,
                    username: null,
                    password: null,
                    passwordIsChanged: false
                },
                // Fields to edit "terugmeldingen" label in the menu
                feedbackLabelText: '',
                // Keep the original value to determine if the value has been changed and needs saving.
                feedbackLabelTextOriginal: ''
            };
        },

        computed: {
            isUnavailable() {
                return this.record.unavailable;
            },

            isBtnCalculateDisabled() {
                if (!this.record || !this.record.coordinates) {
                    return true;
                } else {
                    return (
                        !this.polygonIsValid || this.record.coordinates.length < 15
                    );
                }
            },

            /**
             * Check if organization can be saved
             * @returns {boolean} indicating if organization can be saved
             */
            isBtnSaveDisabled: function () {
                return (
                    !this.validStateEmail ||
                    !this.validStateNewsEmail ||
                    !this.validStateSenderEmail ||
                    this.isSaving ||
                    this.isDeleting ||
                    this.organizationNameInUse ||
                    this.organizationCodeInUse ||
                    this.feedbackUserNameAvailable === false ||
                    !this.polygonIsValid ||
                    this.errorInvalidFormat ||
                    this.errorInvalidBBox ||
                    this.errorInvalidGeom ||
                    this.isUnavailable
                );
            },

            isBtnRemoveDisabled() {
                return this.isUnavailable;
            },

            showEyeIcon() {
                return (
                    (!this.superUserEditMode && !this.adminSettingsMode) ||
                    this.record.passwordIsChanged === true
                );
            },

            showServicesEyeIcon() {
                return this.servicesAuthentication.passwordIsChanged === true;
            },

            feedbackUserNameIsReadonly() {
                // admin can not change username
                // superUser can not edit the username, only set the username for a new organization
                return this.superUserEditMode || this.adminSettingsMode;
            },

            feedbackUserPasswordIsReadonly() {
                // admin can allways change the password
                // superUser can not change or set the password
                return !this.adminSettingsMode;
            },

            hasWebLicense() {
                const webLicense = this.licenses.find(
                    (license) => license.name.toLowerCase() === 'web'
                );
                const hasLicense =
                    webLicense !== undefined && webLicense.hasLicense === true;
                return hasLicense;
            },

            hasMobileLicense() {
                const mobileLicense = this.licenses.find(
                    (license) => license.name.toLowerCase() === 'mobile'
                );
                var hasLicense =
                    mobileLicense !== undefined &&
                    mobileLicense.hasLicense === true;
                // for new organizations, get new urlConnectionLicenses
                if (
                    hasLicense &&
                    (!this.urlConnectionLicenses ||
                        this.urlConnectionLicenses.length === 0)
                ) {
                    this.$_getUrlConnectionLicenses();
                }
                return hasLicense;
            },
            hasStufLicense() {
                const stufLicense = this.localApiLicenses.find(
                    (license) =>
                        license.apiType === EXTERNAL_API_TYPE.STUFF &&
                        license.licensed
                );
                if (stufLicense == undefined) {
                    return false;
                }
                return stufLicense.licensed;
            },
            hasKvkLicense() {
                const kvkLicense = this.localApiLicenses.find(
                    (license) =>
                        license.apiType === EXTERNAL_API_TYPE.KVK &&
                        license.licensed
                );
                if (kvkLicense == undefined) {
                    return false;
                }
                return kvkLicense.licensed;
            },

            menuItemStorageLayersTitle() {
                if (this.adminSettingsMode) {
                    return this.$t('menuItemStorageLayers');
                } else {
                    return this.$t('menuItemStorageLayersNewOrganization');
                }
            },

            maxResults() {
                return MAX_RESULTS_RELATION;
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;
            if (this.isSettings) {
                this.adminSettingsMode = true;
                this.title = this.$t('menuItemSettings');
            } else {
                if (this.id && this.id > 0) {
                    this.superUserEditMode = true;
                    this.title = this.$t('organizationCardDetailEditTitle');
                } else {
                    this.superUserEditMode = false;
                    this.title = this.$t('organizationCardDetailAddTitle');
                }
            }
        },

        mounted() {
            // Retrieve Language codes
            this.$_getAvailableLanguages();
            this.$_getOrganizationBitmaps();
            this.$_getBaseProfiles();
            if (this.adminSettingsMode) {
                this.$_getSettings();
                this.$_getFeedbackLabelText();
            } else {
                if (!this.superUserEditMode) {
                    this.$_getLicenses();
                }
                this.$_getOrganization();
            }
            if (this.userInfo.isSuper) {
                this.$_getDomains();
            }
            this.$_getApiLicenses();
        },

        methods: {
            ...mapActions(['getOrganizationSetting']),
            validator(tag) {
                var res = tag.match(
                    /[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
                );
                return res !== null;
            },
            $_availableFavicons(bitmaps) {
                return availableFavicons(bitmaps);
            },

            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },

            $_availableLogos(bitmaps) {
                return availableLogos(bitmaps);
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_toggleEmailOnErrorOnly(value) {
                this.record.emailOnErrorOnly = value;
            },
            $_toggleGetProxyUsersFromAd(value) {
                this.record.getProxyUsersFromAd = value;
            },
            $_toggleHighlightBorder(value) {
                this.record.highlightBorder = value;
            },

            $_toggleLicense(attribute) {
                attribute.hasLicense = !attribute.hasLicense;
            },

            /**
             * Language selected event handler
             * @param  {[type]} value [description]
             */
            $_onLanguageSelected(value) {
                this.record.language = value;
            },

            /**
             * Language selected event handler
             * @param  {[type]} value [description]
             */
            $_onBaseProfileSelected(value) {
                this.record.ProfileId = value;
            },

            $_onBaseUserGroupSelected(value) {
                this.record.userGroupId = value;
            },

            $_getSettings() {
                nprogress.start();
                // Get existing data from the backend
                this.$store
                    .dispatch('useApiData', {
                        name: 'GetOrganizationSettings'
                    })
                    .then((result) => {
                        this.record = Object.assign({}, result.data?.organization);
                        this.user = Object.assign({}, result.data);
                        this.$_getServicesOwsAuthentication(this.record.id);

                        if (this.record.maxFeatures === 0) {
                            this.record.maxFeatures = 100;
                        }
                        this.$_getAllUserGroups();
                        nprogress.done();
                    });
            },

            async $_getFeedbackLabelText() {
                // Get the saved value from the database.
                this.feedbackLabelTextOriginal = await this.getOrganizationSetting({
                    name: 'feedbackLabelText'
                });
                // If the saved value is not null use that value, otherwise use the standard text
                this.feedbackLabelText = this.feedbackLabelTextOriginal !== this.$t('menuItemFeedbacks') ? this.feedbackLabelTextOriginal : '';
            },

            async $_saveFeedbackLabelText() {
                // Only save when feedbackLabelText has been changed
                const feedbackLabelText = this.feedbackLabelText || this.$t('menuItemFeedbacks');
                if (feedbackLabelText != this.feedbackLabelTextOriginal) {
                    this.$store.dispatch('useApiData', {
                        name: 'SaveOrganizationSetting',
                        data: {
                            name: 'feedbackLabelText',
                            value: feedbackLabelText
                        }
                    });
                }
            },

            $_getLicenses() {
                nprogress.start();
                // Get existing data from the backend
                this.$store
                    .dispatch('useApiData', {
                        name: 'GetLicenses'
                    })
                    .then((result) => {
                        this.$_mapLicenses(result.data);
                        nprogress.done();
                    });
            },

            $_getAllUserGroups() {
                // get all data from the backend
                this.$store
                    .dispatch('useApiData', { name: 'GetUserGroups' })
                    .then((result) => {
                        this.userGroups = this.$_convertToValueTextPair(
                            result.data,
                            'id',
                            'name'
                        );
                    });
            },

            $_mapLicenses(licences) {
                this.licenses = licences;
                this.licenses = orderBy(this.licenses, [
                    (o) => o.name.toLowerCase()
                ]);

                // Add translations
                this.licenses.forEach((item) => {
                    if (item.name === 'Mobile') {
                        item.displayName = this.$t('licenseMobile');
                    } else if (item.name === 'Web') {
                        item.displayName = this.$t('licenseWeb');
                    } else {
                        item.displayName = this.$t('licensePortal');
                    }
                });
            },

            $_getOrganizationBitmaps() {
                const organizationId = this.adminSettingsMode
                    ? this.userInfo.organizationId
                    : this.id;
                this.$store
                    .dispatch('useApiData', {
                        name: 'GetOrganizationLogos',
                        parameters: { id: organizationId }
                    })
                    .then((result) => {
                        this.organizationBitmaps = Object.assign([], result.data);
                    })
                    .catch((error) => {
                        const data = {
                            message: error.message,
                            variant: 'danger',
                            title: 'Ophalen van logos mislukt',
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                    });
            },

            $_getBaseProfiles() {
                this.$store
                    .dispatch('useApiData', {
                        name: 'GetProfileOverview',
                        parameters: { profileTypeId: 1 }
                    })
                    .then((result) => {
                        this.baseProfiles = Object.assign([], result.data);
                    })
                    .catch((error) => {
                        const data = {
                            message: error.message,
                            variant: 'danger',
                            title: 'Ophalen van ondergrondprofiles mislukt',
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                    });
            },

            async $_getDomains() {
                const organizationId = this.adminSettingsMode
                    ? this.userInfo.organizationId
                    : this.id;

                const DomainsResult = await this.$store.dispatch('useApiData', {
                    name: 'GetOrganizationDomains',
                    parameters: { OrganizationId: organizationId }
                });
                this.domains = DomainsResult.data;
                this.tags = this.domains.map((a) => a.name);
            },

            async $_SaveDomains(organizationId) {
                let bestaat;
                for (const tag of this.tags) {
                    bestaat = this.domains.some(
                        (domain) => domain.name.toLowerCase() === tag.toLowerCase()
                    );

                    // Tag not in domains list: Add
                    if (!bestaat) {
                        const domain = {
                            organizationId: organizationId,
                            name: tag
                        };
                        try {
                            await this.$store.dispatch('useApiData', {
                                name: 'CreateDomain',
                                parameters: undefined,
                                data: domain
                            });
                        } catch (error) {
                            let msg;
                            if (error.status == 409) {
                                msg = this.$t('domains.saveConflictError', [tag]);
                            } else {
                                msg = this.$t('domains.saveError', [tag]);
                            }

                            let messageData;

                            messageData = {
                                message: msg,
                                variant: 'danger',
                                title: this.$t(error.title),
                                autoHideDelay: 8000
                            };

                            this.showToast(messageData);
                            this.isSaving = false;
                        }
                    }
                }

                for (const domain of this.domains) {
                    bestaat = this.tags.some(
                        (tag) => tag.toLowerCase() === domain.name.toLowerCase()
                    );

                    // Domain not in tags list: delete
                    if (!bestaat) {
                        try {
                            await this.$store.dispatch('useApiData', {
                                name: 'DeleteDomain',
                                parameters: { Id: domain.id }
                            });
                        } catch (error) {
                            const messageData = {
                                message: this.$t(error.message),
                                variant: 'danger',
                                title: this.$t(error.title),
                                autoHideDelay: 5000
                            };
                            this.showToast(messageData);
                            this.isSaving = false;
                        }
                    }
                }
            },

            $_getAvailableLanguages() {
                // Retrieve available languages
                this.getRefCodes('LANGUAGE').then((result) => {
                    for (const priorityType of result.data) {
                        this.availableLanguages.push({
                            value: priorityType.code,
                            text: priorityType.description
                        });
                    }
                });
            },

            /*
             * Gets all Api licenses for this organization
             */
            async $_getApiLicenses() {
                let organizationId = this.id ?? -1;

                if (organizationId == -1) {
                    organizationId = this.userInfo.organizationId;
                }
                const licencedApisResult = await this.$store.dispatch(
                    'useApiData',
                    {
                        name: 'GetOrganizationApis',
                        parameters: { OrganizationId: organizationId }
                    }
                );

                const allApisResult = await this.$store.dispatch('useApiData', {
                    name: 'GetApis'
                });
                const licencedApiIds = licencedApisResult.data.map(
                    ({ id, organizationApiSettingsId }) => ({
                        id,
                        organizationApiSettingsId
                    })
                );

                this.apiLicenses = allApisResult.data
                    .map((api) => {
                        const foundLicence = licencedApiIds.find(
                            (licencedApi) => licencedApi.id === api.id
                        );
                        return {
                            id: api.id,
                            name: api.name,
                            licensed: !!foundLicence,
                            organizationApiSettingsId:
                                foundLicence?.organizationApiSettingsId,
                            apiType: api.apiType
                        };
                    })
                    .sort(arraySortAscendingFunction('name', false));
                this.localApiLicenses = JSON.parse(
                    JSON.stringify(this.apiLicenses)
                );
            },

            async $_getOrganization() {
                if (this.superUserEditMode) {
                    nprogress.start();
                    await this.$store
                        .dispatch('useApiData', {
                            name: 'GetOrganization',
                            parameters: { id: this.id }
                        })
                        .then((result) => {
                            // Make object properties reactive for Vue
                            this.record = Object.assign({}, result.data);
                            this.logo.id = this.record.id;
                            this.$_mapLicenses(this.record.licenses);
                            this.$_getUrlConnectionLicenses();
                            this.$_getServicesOwsAuthentication(this.id);
                            this.isCodeReadonly =
                                this.record.code && this.record.code.length > 0;
                            nprogress.done();
                        });
                } else {
                    // Default settings for new org
                    this.record = {
                        emailOnErrorOnly: true,
                        highlightBorder: true,
                        language: null,
                        maxFeatures: 100,
                        coordinates: null,
                        minX: null,
                        minY: null,
                        maxX: null,
                        maxY: null,
                        senderEmail: null,
                        senderEmailName: null,
                        newsEmail: null,
                        feedbackPassword: '*****'
                    };
                }
            },

            /*
             * Gets all url connection licencses
             */
            async $_getUrlConnectionLicenses() {
                const organizationId = this.record.id ? this.record.id : -1;
                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetAllOrganizationUrlConnections',
                        parameters: { id: organizationId }
                    })
                    .then((result) => {
                        this.urlConnectionLicenses = Object.assign([], result.data);
                        this.urlConnectionLicenses = orderBy(
                            this.urlConnectionLicenses,
                            [(o) => o.urlConnectionLicense.name.toLowerCase()]
                        );
                    })
                    .catch((error) => {
                        const data = {
                            message: error.message,
                            variant: 'danger',
                            title: 'Ophalen van Verbindingslicentie mislukt',
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                    });
            },

            async $_getServicesOwsAuthentication(organizationId) {
                try {
                    const owsAuthenticationResult = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetOwsAuthentication',
                            parameters: { OrganizationId: organizationId }
                        }
                    );
                    this.servicesAuthentication = Object.assign(
                        {},
                        owsAuthenticationResult.data
                    );
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t(
                            'organization.errorGetServicesAuthentication'
                        ),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            $_exists: debounce(async function (searchTerm) {
                const organizationParams = {
                    name: searchTerm,
                    orgId: this.id && this.id > 0 ? this.id : 0
                };

                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetOrganizationNameExists',
                        data: organizationParams
                    })
                    .then((exists) => {
                        this.organizationNameInUse = exists.data;
                    });
            }, 750),

            $_codeExists: debounce(async function (searchTerm) {
                const organizationParams = {
                    code: searchTerm,
                    orgId: this.id && this.id > 0 ? this.id : 0
                };

                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetOrganizationCodeExists',
                        data: organizationParams
                    })
                    .then((exists) => {
                        this.organizationCodeInUse = exists.data;
                    });
            }, 750),

            async $_feedbackUserNameExists(username) {
                this.record.feedbackUsername = username;
                const organizationParams = {
                    name: this.record.feedbackUsername,
                    orgId: this.id && this.id > 0 ? this.id : 0
                };

                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetFeedbackUserNameExists',
                        data: organizationParams
                    })
                    .then((exists) => {
                        this.feedbackUserNameAvailable = !exists.data;
                    });
            },

            $_onAuthPasswordChanged(password) {
                this.record.feedbackPassword = password;
                this.record.passwordIsChanged = true;
            },

            $_onServicesUsernameChanged(username) {
                // user name for NedGlobe Services is fixed
            },

            $_onServicesPasswordChanged(password) {
                this.servicesAuthentication.password = password;
                this.servicesAuthentication.passwordIsChanged = true;
            },

            $_onLogoSelected(input) {
                this.record.logoId = input;
            },

            $_onFaviconSelected(input) {
                this.record.faviconId = input;
            },

            $_OnModalBitmapSaved(savedBitmap) {
                if (savedBitmap) {
                    // Add newly created bitmap to the set
                    this.organizationBitmaps.push(savedBitmap);
                    // Assign newly created bitmap to the organization logo or favicon
                    if (savedBitmap.imageType === AVAILABLE_LOGO_TYPE[0].value) {
                        this.record.logoId = savedBitmap.id;
                    } else if (
                        savedBitmap.imageType === AVAILABLE_LOGO_TYPE[1].value
                    ) {
                        this.record.faviconId = savedBitmap.id;
                    }
                }
            },

            $_openAddBitmapModal(imageType) {
                // TODO This is a strange contruction:
                // Modal dialog spawns every time boolean value of launchModal changes
                this.launchModal = !this.launchModal;
                this.modalSelectedImageType = imageType;
            },

            /**
             * Bereken de omhullende rechthoek uit de opgegeven polygoon.
             * LO: minX, minY
             * RB: maxX, maxY
             */
            $_calculateBBox: function () {
                // bepaal de boundingbox uit de polygon coordinaten
                const bbox = this.$polygonService.calculateBBox(
                    this.record.coordinates
                );
                if (bbox && bbox.length === 4) {
                    this.record.minX = bbox[0];
                    this.record.minY = bbox[1];
                    this.record.maxX = bbox[2];
                    this.record.maxY = bbox[3];
                }
                // valideer berekening
                this.$_validateBBox();
            },

            /**
             * Valideer de omhullende rechthoek
             * eis: minX < maxX en minY < maxY
             * @return {bool} true als de rechthoek valide is, anders false
             */
            $_validateBBox: function () {
                const bbox = [
                    this.record.minX,
                    this.record.minY,
                    this.record.maxX,
                    this.record.maxY
                ];
                const isValid = this.$polygonService.validateBBox(bbox);
                if (isValid) {
                    this.errorInvalidBBox = false;
                } else {
                    this.errorInvalidBBox = true;
                }
                return isValid;
            },

            /**
             * Valideer de polygoon invoer
             * @return {Boolean} true als de invoer correct gevalideerd is anders false
             */
            $_isValidPolygon: function () {
                this.$polygonService.validatePolygon(this.record.coordinates).then(
                    (result) => {
                        this.polygonIsValid = result;
                        if (!this.polygonIsValid) {
                            this.errorInvalidFormat = true;
                        } else {
                            this.errorInvalidFormat = false;
                            this.errorInvalidGeom = false;
                        }
                        return this.polygonIsValid;
                    },
                    (e) => {
                        // Validation failed
                        this.polygonIsValid = false;
                        return this.polygonIsValid;
                    }
                );
            },

            $_fillNewsEmail: function () {
                if (
                    !this.record.newsEmail ||
                    this.record.newsEmail === this.record.news_alias
                ) {
                    this.record.newsEmail = this.record.news_alias =
                        this.record.email;
                } else {
                    this.record.news_alias = this.record.email;
                }
            },

            $_gotoHome() {
                this.$router.push({ path: '/' });
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/organizations' });
            },

            $_onOpenGeoServer() {
                // backend accepts GeoServer web uri only when ending on /
                const geoServerWebUrl = `${this.servicesAuthentication.geoServerUrl}/web/?0`;
                // open GeoServer web interface in a new browser tab
                window.open(geoServerWebUrl, '_blank').focus();
            },

            /**
             * Set available flag to false if licensed (input) flag is false.
             * Don't set available flag to true if licensed is set to true.
             * @param  {bool}   input [licensed state value]
             * @param  {object} item  [OrganizationUrlConnection record object]
             */
            $_onChangeLicensed(input, item) {
                if (input === false) {
                    item.available = input;
                }
            },

            $_onSubmit() {
                if (this.$refs.organizationform.checkValidity()) {
                    this.$_onSave(false);
                }
            },

            async $_onSave() {
                this.isSaving = true;

                // Save the settings /organization
                let saveFunction;
                if (this.adminSettingsMode) {
                    this.user.organization_id = this.userInfo.organizationId;
                    this.user.organization = this.record;
                    const organization = Object.assign({}, this.record);

                    saveFunction = await this.$store.dispatch('useApiData', {
                        name: 'SaveOrganizationSaveSettings',
                        data: organization
                    });

                    this.$_saveFeedbackLabelText();
                } else {
                    this.record.licenses = this.licenses;
                    const organization = Object.assign({}, this.record);

                    if (parseInt(organization.userDeleteWait) < 0) {
                        this.record.userDeleteWait = organization.userDeleteWait =
                            '0';
                    }

                    saveFunction = await this.$store.dispatch('useApiData', {
                        name: 'SaveOrganization',
                        data: organization
                    });
                }

                this.$_continueSave(saveFunction);

                // reset password presentation
                if (this.record.feedbackPassword) {
                    this.$emit('password-toggle-reset', [
                        this.record.feedbackPassword
                    ]);
                }
            },

            /**
             * Save urlConnectionLicenses for this organization
             * @param  {object} organization          [previously saved organization]
             * @param  {array} urlConnectionLicenses  [array of urlConnectionLicense objects]
             */
            async $_SaveOrganizationUrlConnections(
                organization,
                urlConnectionLicenses
            ) {
                // set organization id for new organizations
                const updatedUrlConnectionLicenses = urlConnectionLicenses.map(
                    (l) => {
                        // are these records for a new organization?
                        if (l.organizationId === -1) {
                            l.id = -1;
                            l.organizationId = organization.id;
                        }
                        return l;
                    }
                );

                await this.$store
                    .dispatch('useApiData', {
                        name: 'SaveOrganizationUrlConnections',
                        data: updatedUrlConnectionLicenses
                    })
                    .then(() => {
                        // Save all licences succeeded
                    })
                    .catch((error) => {
                        const data = {
                            message: 'Save all licences  failed.',
                            variant: 'danger',
                            title: this.$t('saveFailureTitle'),
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                    });
            },

            async $_continueSave(saveFunction) {
                if (saveFunction) {
                    try {
                        if (!saveFunction?.data || !saveFunction?.data?.id) {
                            this.isSaving = false;
                            return;
                        }

                        if (this.userInfo.isSuper && !this.adminSettingsMode) {
                            await this.$_SaveSuperUserMode(saveFunction.data);
                        } else {
                            await this.$_SaveAdminMode(saveFunction.data);
                        }

                        let toastData = {
                            message: this.adminSettingsMode
                                ? this.$t('settingsCardSavedConfirmation')
                                : this.$t('organizationCardSavedConfirmation'),
                            variant: 'success',
                            autoHideDelay: 2500
                        };
                        this.showToast(toastData);
                        this.$root.$emit('organisation_changed');

                        this.isSaving = false;
                    } catch (error) {
                        const data = {
                            message: this.$t(error.message),
                            variant: 'danger',
                            title: this.$t('saveFailureTitle'),
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                        this.isSaving = false;
                    }
                }
            },

            /**
             * Additional save action for super-user mode
             */
            async $_SaveSuperUserMode(data) {
                this.$_SaveDomains(data.id);
                this.$_SaveApiLicenses(data.id);
                // save url connections
                this.$_SaveOrganizationUrlConnections(
                    data,
                    this.urlConnectionLicenses
                );
                // save NedGlobe Services
                await this.$_SaveNedGlobeServices(data.id);

                if (!this.superUserEditMode) {
                    // on a new organization
                    await this.$_CreateFeedbackStore(
                        data.id,
                        this.hasMobileLicense,
                        this.hasWebLicense
                    );
                    await this.$_CreateFeedbackUser(data.id);

                    // new organization goto overview
                    this.$_gotoOverview();
                } else {
                    await this.$_UpdateFeedbackStore(
                        data.id,
                        this.hasMobileLicense,
                        this.hasWebLicense
                    );

                    // existing organization reload the view
                    if (data.id === this.userInfo.organizationId) {
                        const toastData = {
                            message: this.$t('organizationCardReload'),
                            variant: 'success',
                            autoHideDelay: 5000
                        };
                        this.showToast(toastData);
                        setTimeout(() => this.$router.go(), 5000);
                    }
                }
            },

            /**
             * Additional save action for admin mode
             * Only Admin can change passwords
             */
            async $_SaveAdminMode(data) {
                await this.$_UpdateFeedbackUserPassword(data.id);
                await this.$_UpdateNedGlobeServicesPassword(data.id);
            },

            $_updateToken(value) {
                this.record.scimToken = value;
            },

            /*
             * Saves the changes of Api licenses
             */
            async $_SaveApiLicenses(orgId) {
                const changedLicences = this.localApiLicenses.filter(
                    (api, index) =>
                        this.apiLicenses[index].licensed !== api.licensed
                );
                try {
                    await Promise.all(
                        changedLicences.map(
                            ({ id, licensed, organizationApiSettingsId }) => {
                                if (licensed) {
                                    return this.$store.dispatch('useApiData', {
                                        name: 'CreateOrganizationApiSettings',
                                        parameters: {
                                            OrganizationId: orgId,
                                            ApiId: id
                                        }
                                    });
                                }
                                return this.$store.dispatch('useApiData', {
                                    name: 'DeleteOrganizationApiSettings',
                                    parameters: { Id: organizationApiSettingsId }
                                });
                            }
                        )
                    );
                    this.$_getApiLicenses();
                    this.$store.dispatch('getHasAnyApiLicense');
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Create a store in the Feedback GeoServer
             */
            async $_CreateFeedbackStore(orgId, hasMobileLicense, hasWebLicense) {
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'CreateFeedbackStores',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            enableFeedbackStore: hasMobileLicense,
                            enableWebFeedbackStore: hasWebLicense
                        }
                    });
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t('organization.errorCreateFeedbackStore'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Update a store in the Feedback GeoServer
             */
            async $_UpdateFeedbackStore(orgId, hasMobileLicense, hasWebLicense) {
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'UpdateFeedbackStores',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            enableFeedbackStore: hasMobileLicense,
                            enableWebFeedbackStore: hasWebLicense
                        }
                    });
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t('organization.errorUpdateFeedbackStore'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Create a user in the Feedback GeoServer
             */
            async $_CreateFeedbackUser(orgId) {
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'CreateFeedbackUser',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            password: this.record.feedbackPassword
                        }
                    });
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t('organization.errorCreateFeedbackUser'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Update feedback password
             */
            async $_UpdateFeedbackUserPassword(orgId) {
                if (!this.record.passwordIsChanged) {
                    return;
                }

                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'UpdateFeedbackUser',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            password: this.record.feedbackPassword
                        }
                    });
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t('organization.errorUpdateFeedbackPassword'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Save NodGlobe Services user password
             */
            async $_UpdateNedGlobeServicesPassword(orgId) {
                // is password changed?
                if (!this.servicesAuthentication.passwordIsChanged) {
                    return;
                }

                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'UpdateNedGlobeServicesUser',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            password: this.servicesAuthentication.password
                        }
                    });
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('organization.errorUpdateServicesPassword'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            /**
             * Initialize and Save NodGlobe Services connection
             */
            async $_SaveNedGlobeServices(orgId) {
                if (!this.record.hasServicesConnection) {
                    return;
                }

                const self = this;
                try {
                    // create a NedGlobe Services GeoServer authentication for the organizations admin user
                    await self.$store.dispatch('useApiData', {
                        name: 'CreateNedGlobeServicesUser',
                        parameters: undefined,
                        data: {
                            organizationId: orgId,
                            username: this.servicesAuthentication.username,
                            password: this.servicesAuthentication.password
                        }
                    });
                    // create a new PostgreSql schema if not exists
                    await self.$store.dispatch('useApiData', {
                        name: 'CreateGeoServerDbSchema',
                        parameters: {
                            OrganizationId: orgId
                        }
                    });
                    // create a new GeoServer data source to the PostgreSql schema if not exists
                    await self.$store.dispatch('useApiData', {
                        name: 'CreateNedGlobeServicesStore',
                        parameters: undefined,
                        data: {
                            OrganizationId: orgId,
                            username: this.servicesAuthentication.username,
                            password: this.servicesAuthentication.password
                        }
                    });
                    // create a new NedGlobeServices OWS if not exists
                    await self.$store.dispatch('useApiData', {
                        name: 'CreateNedGlobeServicesOws',
                        parameters: {
                            OrganizationId: orgId
                        }
                    });
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: self.$t(
                            'organization.errorInitializeNedGlobeServices'
                        ),
                        autoHideDelay: 5000
                    };
                    self.showToast(data);
                }
            },

            /**
             * Remove organization
             */
            $_onRemove: async function () {
                if (
                    this.userInfo.isSuper &&
                    this.userInfo.organizationId === this.record.id
                ) {
                    this.msgBoxOk({
                        message: this.$t('organizationCardDeleteErrorSuper')
                    });
                    return;
                }

                const dataMessage = {
                    message: this.$t('organizationCardDeleteConfirmation')
                };

                this.msgBoxConfirm(dataMessage).then(async (confirm) => {
                    if (confirm) {
                        this.isDeleting = true;

                        this.record.licenses = this.licenses;
                        const organization = Object.assign({}, this.record);
                        // delete organization
                        await this.$store
                            .dispatch('useApiData', {
                                name: 'DeleteOrganization',
                                parameters: { organizationId: organization.id }
                            })
                            .then(async (result) => {
                                if (result.data) {
                                    const data = {
                                        message: this.$t(
                                            'organization.deleteScheduledInformation'
                                        ),
                                        variant: 'info',
                                        title: this.$t(
                                            'organization.deleteScheduledTitle'
                                        ),
                                        autoHideDelay: 5000
                                    };
                                    this.showToast(data);

                                    this.$root.$emit('organisation_changed');
                                    // reload organization data
                                    this.$_getOrganization();
                                } else {
                                    const data = {
                                        message: this.$t(
                                            'organizationCardDeleteErrorLinked'
                                        ),
                                        variant: 'danger',
                                        title: this.$t('deleteFailureTitle'),
                                        autoHideDelay: 10000
                                    };
                                    this.showToast(data);
                                }

                                this.isDeleting = false;
                            })
                            .catch((error) => {
                                const data = {
                                    message: this.$t('deleteFailureMessage', [
                                        `${error.message}`
                                    ]),
                                    variant: 'danger',
                                    title: this.$t('deleteFailureTitle'),
                                    autoHideDelay: 10000
                                };
                                this.showToast(data);
                                this.isDeleting = false;
                            });
                    }
                });
            },

            $_onCancel: function () {
                if (this.adminSettingsMode) {
                    this.$_gotoHome();
                } else {
                    this.$_gotoOverview();
                }
            }
        }
    };
</script>

<style scoped>
.form-group.row {
    margin-left: 0px;
}

.container {
    padding-left: 0px;
}

.childblock {
    margin-left: 25px;
    margin-top: 10px;
    margin-bottom: 10px;
}
</style>
<!-- If we add "scoped" attribute to limit CSS to this component only
     the hide of the header will not work! -->
<style>
.license-cell-width {
    width: 62%;
}
</style>
